// SUB-OFFICE 7 — pneumatic canisters. One instanced pool (cap 12) of amber
// slugs riding the ceiling splines. Two duties:
//   PLAYER CANISTER — launched at the plunger slam, rides mouth -> trunk ->
//   Vault, arrival timed to the ceremony tier (t=4s full / t=2s expedited),
//   clunking per fixture ring on the way.
//   AMBIENT — the planet always sounds at least sparsely processed: slugs on
//   the trunk at max(floor rate, global judgment rate from the stats bus).
// Also builds the TUBE MOUTH fixture (brass pillar + plunger) on the writing
// floor — the terminal step of the submit chain.

import {
  CatmullRomCurve3, Color, CylinderGeometry, DynamicDrawUsage, Group,
  InstancedMesh, Matrix4, Mesh, MeshBasicMaterial, MeshLambertMaterial,
  Object3D, Vector3,
} from 'three';
import { PAL, shade } from '../palette';
import { TUBES, TUBE_MOUTH, VAULT } from '../world/layout';
import { patchMaterial } from '../render/scene';
import type { Entity, EventBus } from './entities';
import type { StatsBlob } from '../../shared/types';

const CAP = 12;
const RING_M = 4; // brass fixture rings every 4m: one clunk each

interface Slug {
  active: boolean;
  curve: CatmullRomCurve3;
  length: number;
  u: number;
  speed: number; // u per second
  nextClunkM: number;
  isPlayer: boolean;
}

export class Canisters implements Entity {
  readonly id = 'canisters';
  readonly group = new Group();

  private mesh: InstancedMesh;
  private slugs: Slug[] = [];
  private dummy = new Object3D();
  private mat4 = new Matrix4();
  private tmp = new Vector3();
  private tmp2 = new Vector3();
  private trunk: CatmullRomCurve3;
  private trunkLen: number;
  private spawnTimer = 4;
  private spawnInterval = 7; // dignified floor rate; stats modulates downward
  private plunger: Mesh;
  private plungerPop = 0;

  constructor(private bus: EventBus) {
    // trunk spline (layout TUBES[0]): window row -> the Vault mouth
    const trunkPts = TUBES[0].map(([x, y, z]) => new Vector3(x, y, z));
    this.trunk = new CatmullRomCurve3(trunkPts);
    this.trunkLen = this.trunk.getLength();

    const geo = new CylinderGeometry(0.11, 0.11, 0.42, 10, 1);
    geo.rotateZ(Math.PI / 2); // slugs travel on their sides
    const mat = new MeshBasicMaterial({ color: new Color(PAL.amber).convertSRGBToLinear() });
    patchMaterial(mat, { dimExempt: true }); // in-flight offerings stay lit
    this.mesh = new InstancedMesh(geo, mat, CAP);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = CAP;
    this.group.add(this.mesh);
    for (let i = 0; i < CAP; i++) {
      this.slugs.push({
        active: false, curve: this.trunk, length: this.trunkLen,
        u: 0, speed: 0, nextClunkM: RING_M, isPlayer: false,
      });
      this.park(i);
    }
    this.mesh.instanceMatrix.needsUpdate = true;

    // ---- the tube mouth: brass pillar + carbon plunger cap ----------------
    const pillarMat = new MeshLambertMaterial({
      color: new Color(PAL.brass).convertSRGBToLinear(), flatShading: true,
    });
    patchMaterial(pillarMat);
    const pillar = new Mesh(new CylinderGeometry(0.17, 0.2, 1.35, 10, 1), pillarMat);
    pillar.position.set(TUBE_MOUTH.x, 0.675, TUBE_MOUTH.z);
    this.group.add(pillar);
    const collarMat = new MeshBasicMaterial({ color: new Color(shade(PAL.brass, 0.25)) });
    patchMaterial(collarMat);
    const collar = new Mesh(new CylinderGeometry(0.23, 0.23, 0.08, 10, 1), collarMat);
    collar.position.set(TUBE_MOUTH.x, 1.32, TUBE_MOUTH.z);
    this.group.add(collar);
    const plungerMat = new MeshLambertMaterial({
      color: new Color(shade(PAL.carbon, 0.12)).convertSRGBToLinear(), flatShading: true,
    });
    patchMaterial(plungerMat);
    this.plunger = new Mesh(new CylinderGeometry(0.14, 0.14, 0.22, 10, 1), plungerMat);
    this.plunger.position.set(TUBE_MOUTH.x, 1.5, TUBE_MOUTH.z);
    this.group.add(this.plunger);

    bus.on('stats', (p) => {
      const s = p as StatsBlob;
      const rate = typeof s.judgment_rate_per_min === 'number' ? s.judgment_rate_per_min : 0;
      // rate/min -> seconds between ambient slugs, clamped to stay dignified
      this.spawnInterval = rate > 0 ? Math.min(12, Math.max(2.5, 60 / rate)) : 7;
    });
  }

  private park(i: number): void {
    this.dummy.position.set(0, -50, 0);
    this.dummy.updateMatrix();
    this.mat4.copy(this.dummy.matrix);
    this.mesh.setMatrixAt(i, this.mat4);
  }

  private free(): number {
    for (let i = 0; i < CAP; i++) if (!this.slugs[i].active) return i;
    return -1;
  }

  /** The plunger slam: launch the player's offering; Vault arrival at t=durationS. */
  launchPlayerCanister(durationS: number): void {
    const i = this.free();
    if (i < 0) return;
    const s = this.slugs[i];
    const pts = [
      new Vector3(TUBE_MOUTH.x, 1.45, TUBE_MOUTH.z),
      new Vector3(TUBE_MOUTH.x, 4.6, TUBE_MOUTH.z - 2),
      new Vector3(TUBE_MOUTH.x, 5.5, -15.9),
      new Vector3(-4, 5.5, -15.9),
      new Vector3(-14, 5.5, -15.9),
      new Vector3(-24, 5.8, -10),
      new Vector3(-28.5, 6.2, -3),
      new Vector3(VAULT.x + 0.7, 6.8, VAULT.z),
    ];
    s.curve = new CatmullRomCurve3(pts);
    s.length = s.curve.getLength();
    s.u = 0;
    s.speed = 1 / Math.max(0.5, durationS);
    s.nextClunkM = RING_M;
    s.isPlayer = true;
    s.active = true;
    this.plungerPop = 1;
    this.bus.emit('audio:clunk');
  }

  private launchAmbient(): void {
    const i = this.free();
    if (i < 0) return;
    const s = this.slugs[i];
    s.curve = this.trunk;
    s.length = this.trunkLen;
    s.u = 0;
    s.speed = 1 / (this.trunkLen / 9); // ~9 m/s through the ceiling
    s.nextClunkM = RING_M;
    s.isPlayer = false;
    s.active = true;
  }

  update(dt: number): void {
    // plunger: full speed down, mechanism snap back
    if (this.plungerPop > 0) {
      this.plungerPop = Math.max(0, this.plungerPop - dt * 6);
      this.plunger.position.y = 1.5 - 0.12 * this.plungerPop;
    }

    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0) {
      this.spawnTimer = this.spawnInterval * (0.75 + Math.random() * 0.5);
      this.launchAmbient();
    }

    let dirty = false;
    for (let i = 0; i < CAP; i++) {
      const s = this.slugs[i];
      if (!s.active) continue;
      s.u = Math.min(1, s.u + s.speed * dt);
      const traveled = s.u * s.length;
      if (traveled >= s.nextClunkM) {
        s.nextClunkM += RING_M;
        // ambient slugs clunk quietly; the player's own file clunks with weight
        this.bus.emit('audio:clunk', { player: s.isPlayer });
      }
      s.curve.getPointAt(s.u, this.tmp);
      s.curve.getTangentAt(Math.min(0.999, s.u), this.tmp2);
      this.dummy.position.copy(this.tmp);
      this.tmp2.add(this.tmp);
      this.dummy.lookAt(this.tmp2);
      this.dummy.rotateY(Math.PI / 2);
      this.dummy.updateMatrix();
      this.mat4.copy(this.dummy.matrix);
      this.mesh.setMatrixAt(i, this.mat4);
      dirty = true;
      if (s.u >= 1) {
        s.active = false;
        this.park(i);
        if (s.isPlayer) this.bus.emit('canister:vault');
      }
    }
    if (dirty) this.mesh.instanceMatrix.needsUpdate = true;
  }
}
